import React, {createContext, useContext, useEffect, useState} from "react";
import {DEFAULT_GENRE, normalizeGenre} from "../utilities/themeColors";
import CyberLogoGlitch from "../components/CyberLogoGlitch";
import {setAiArtEnabled} from "../utilities/tokenArt";

const ThemeContext = createContext({
    lightMode: false, setLightMode: () => {},
    showTooltips: true, setShowTooltips: () => {},
    tavernPanZoomEnabled: true, setTavernPanZoomEnabled: () => {},
    tavernOneClickNav: false, setTavernOneClickNav: () => {},
    showAiArt: true, setShowAiArt: () => {},
    genre: DEFAULT_GENRE, setGenre: () => {},
});

// Light mode on/off switch for the whole app: false hides the Global Settings switch and gives
// everyone dark mode, keeping (not overwriting) any saved preference.
export const LIGHT_MODE_ENABLED = true;

// Device-wide copy of the genre, read before anyone logs in (the per-user keys need a username).
const DEVICE_GENRE_KEY = "genre_device";
const readDeviceGenre = () => {
    try { return normalizeGenre(localStorage.getItem(DEVICE_GENRE_KEY)); } catch { return DEFAULT_GENRE; }
};
const writeDeviceGenre = (genre) => {
    try { localStorage.setItem(DEVICE_GENRE_KEY, genre); } catch { /* private mode */ }
};

export function ThemeProvider({children}) {
    const [savedLightMode, setLightModeState] = useState(false);
    const lightMode = LIGHT_MODE_ENABLED && savedLightMode;
    const [showTooltips, setShowTooltipsState] = useState(true);
    const [tavernPanZoomEnabled, setTavernPanZoomEnabledState] = useState(true);
    // Tavern 3D view: a hotspot click opens its page straight away, like the 2D tavern's buttons.
    const [tavernOneClickNav, setTavernOneClickNavState] = useState(false);
    // The bundled AI-generated creature pictures (see utilities/tokenArt, which this keeps in step).
    const [showAiArt, setShowAiArtState] = useState(true);
    // 'fantasy' | 'scifi' | 'horror' | 'postapoc' | 'pirate' | 'urban' | 'japan' | 'cyberpunk' - which palette and icon set the app wears (see utilities/themeColors).
    // Starts on this device's last-used genre, so pages with no user yet (login, sign-up,
    // password reset, invite) wear it too instead of always falling back to fantasy.
    const [genre, setGenreState] = useState(readDeviceGenre);

    useEffect(() => {
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (!userJSON) return;

        // Instant paint from the local cache while the backend fetch is in flight.
        const storedLight = localStorage.getItem("lightMode_" + userJSON.username) === 'true';
        setLightModeState(storedLight);
        const storedTooltips = localStorage.getItem("showTooltips_" + userJSON.username);
        if (storedTooltips !== null) setShowTooltipsState(storedTooltips === 'true');
        const storedPanZoom = localStorage.getItem("tavernPanZoomEnabled_" + userJSON.username);
        if (storedPanZoom !== null) setTavernPanZoomEnabledState(storedPanZoom === 'true');
        setTavernOneClickNavState(localStorage.getItem("tavernOneClickNav_" + userJSON.username) === 'true');
        setShowAiArtState(localStorage.getItem("showAiArt_" + userJSON.username) !== 'false');
        const storedGenre = localStorage.getItem("genre_" + userJSON.username);
        if (storedGenre !== null) { setGenreState(normalizeGenre(storedGenre)); writeDeviceGenre(normalizeGenre(storedGenre)); }

        // Backend is the source of truth (e.g. after switching devices) — reconcile once it responds.
        const headers = {'Authorization': 'Bearer ' + userJSON.accessToken};
        fetch(`${process.env.REACT_APP_API_PROXY}/settings/getSettingsForUser`, {headers})
            .then(r => r.json())
            .then(data => {
                if (data.lightMode !== undefined) {
                    setLightModeState(!!data.lightMode);
                    localStorage.setItem("lightMode_" + userJSON.username, String(!!data.lightMode));
                }
                if (data.showTooltips !== undefined) {
                    setShowTooltipsState(data.showTooltips !== false);
                    localStorage.setItem("showTooltips_" + userJSON.username, String(data.showTooltips !== false));
                }
                if (data.tavernPanZoomEnabled !== undefined) {
                    setTavernPanZoomEnabledState(data.tavernPanZoomEnabled !== false);
                    localStorage.setItem("tavernPanZoomEnabled_" + userJSON.username, String(data.tavernPanZoomEnabled !== false));
                }
                // Null until the user first saves it, which is off.
                if (data.tavernOneClickNav !== undefined) {
                    setTavernOneClickNavState(data.tavernOneClickNav === true);
                    localStorage.setItem("tavernOneClickNav_" + userJSON.username, String(data.tavernOneClickNav === true));
                }
                // Null until the user first saves it, which is on.
                if (data.showAiArt !== undefined) {
                    setShowAiArtState(data.showAiArt !== false);
                    localStorage.setItem("showAiArt_" + userJSON.username, String(data.showAiArt !== false));
                }
                // Null for every row saved before the setting existed, which is fantasy.
                if (data.uiGenre !== undefined) {
                    const serverGenre = normalizeGenre(data.uiGenre);
                    setGenreState(serverGenre);
                    localStorage.setItem("genre_" + userJSON.username, serverGenre);
                    writeDeviceGenre(serverGenre);
                }
            })
            .catch(() => {});
    }, []);

    useEffect(() => { setAiArtEnabled(showAiArt); }, [showAiArt]);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', lightMode ? 'light' : 'dark');
    }, [lightMode]);

    // theme/genre.css keys the other genres' palettes off this attribute.
    useEffect(() => {
        document.documentElement.setAttribute('data-genre', genre);
    }, [genre]);

    const setGenre = (value) => {
        const next = normalizeGenre(value);
        setGenreState(next);
        writeDeviceGenre(next);
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (userJSON) localStorage.setItem("genre_" + userJSON.username, next);
    };

    const setLightMode = (value) => {
        setLightModeState(value);
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (userJSON) localStorage.setItem("lightMode_" + userJSON.username, String(value));
    };

    const setShowTooltips = (value) => {
        setShowTooltipsState(value);
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (userJSON) localStorage.setItem("showTooltips_" + userJSON.username, String(value));
    };

    const setTavernPanZoomEnabled = (value) => {
        setTavernPanZoomEnabledState(value);
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (userJSON) localStorage.setItem("tavernPanZoomEnabled_" + userJSON.username, String(value));
    };

    const setTavernOneClickNav = (value) => {
        setTavernOneClickNavState(value);
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (userJSON) localStorage.setItem("tavernOneClickNav_" + userJSON.username, String(value));
    };

    const setShowAiArt = (value) => {
        setShowAiArtState(value);
        const userJSON = JSON.parse(sessionStorage.getItem('user'));
        if (userJSON) localStorage.setItem("showAiArt_" + userJSON.username, String(value));
    };

    return (
        <ThemeContext.Provider value={{
            lightMode, setLightMode,
            showTooltips, setShowTooltips,
            tavernPanZoomEnabled, setTavernPanZoomEnabled,
            tavernOneClickNav, setTavernOneClickNav,
            showAiArt, setShowAiArt,
            genre, setGenre,
        }}>
            {genre === 'cyberpunk' && <CyberLogoGlitch/>}
            {children}
        </ThemeContext.Provider>
    );
}

export function useLightMode() {
    return useContext(ThemeContext);
}
